import { downloadBlob } from '../download';
import { getApiClient } from '../api-client-factory';
import { API_ENDPOINTS } from '../api-endpoints';
import type { ApiResponse, PaginatedResponse } from '../api-types';
import type {
  MessageType,
  ConversationType,
  ConversationParticipant,
  ConversationMessage,
  ConversationSummary,
  ConversationPeer,
  LinkPreview,
  MessageAttachment,
  MessageInfo,
  MessageRecipient,
  MessageRecipientKind,
  MessageReportReason,
  MessageSearchResult,
  MessagingPrivacy,
  SharedItem,
  StarredMessageItem,
} from '@welynk/types';

// ─── Types ────────────────────────────────────────────────

export type {
  MessageType,
  ConversationType,
  ConversationParticipant,
  ConversationMessage,
  ConversationPeer,
  LinkPreview,
  MessageAttachment,
  MessageInfo,
  MessageRecipient,
  MessageRecipientKind,
  MessageReportReason,
  MessageSearchResult,
  MessagingPrivacy,
  SharedItem,
  StarredMessageItem,
};

// The backend calls this ConversationSummary; kept as `Conversation` here to
// avoid a large rename across the pages that already import it by this name.
export type Conversation = ConversationSummary;

export type InterviewSlotType = 'VIDEO' | 'PHONE' | 'IN_PERSON' | 'TECHNICAL' | 'PANEL';

/** What an interview-times message carries. */
export interface InterviewSlotsMetadata {
  kind: 'interview-slots';
  applicationId: string;
  organizationId: string;
  proposedById: string;
  slots: string[];
  type: InterviewSlotType;
  durationMins: number;
  location: string | null;
  meetingLink: string | null;
  notes: string | null;
  status: 'OPEN' | 'BOOKED' | 'CANCELLED';
  pickedSlot: string | null;
  interviewId: string | null;
}

export interface GetMessagesParams {
  page?: number;
  pageSize?: number;
}

// ─── Service ──────────────────────────────────────────────

export class MessageApiService {
  /** The personal inbox; `archived` lists only archived threads instead. */
  static async getConversations(archived = false): Promise<Conversation[]> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<Conversation[]>>(
      API_ENDPOINTS.MESSAGES.LIST_CONVERSATIONS,
      archived ? { params: { archived: 'true' } } : undefined
    );
    return response.data.data ?? [];
  }

  // ─── Company Messages page ───────────────────────────────

  /** This recruiter's company inbox (application threads). */
  static async getCompanyConversations(orgId: string, archived = false): Promise<Conversation[]> {
    const response = await getApiClient().get<ApiResponse<Conversation[]>>(
      API_ENDPOINTS.MESSAGES.COMPANY_CONVERSATIONS(orgId),
      archived ? { params: { archived: 'true' } } : undefined
    );
    return response.data.data ?? [];
  }

  /** What this recruiter can do from the company inbox. */
  static async getCompanyCapabilities(orgId: string): Promise<{ canScheduleInterviews: boolean }> {
    const response = await getApiClient().get<ApiResponse<{ canScheduleInterviews: boolean }>>(
      API_ENDPOINTS.MESSAGES.COMPANY_CAPABILITIES(orgId)
    );
    return response.data.data;
  }

  /** Offer up to five interview times in an application thread. */
  static async proposeInterviewSlots(
    conversationId: string,
    body: {
      slots: string[];
      type: InterviewSlotType;
      durationMins: number;
      location?: string | null;
      meetingLink?: string | null;
      notes?: string | null;
    }
  ): Promise<ConversationMessage> {
    const response = await getApiClient().post<ApiResponse<ConversationMessage>>(
      API_ENDPOINTS.MESSAGES.INTERVIEW_SLOTS(conversationId),
      body
    );
    return response.data.data;
  }

  static async pickInterviewSlot(messageId: string, slot: string): Promise<ConversationMessage> {
    const response = await getApiClient().post<ApiResponse<ConversationMessage>>(
      API_ENDPOINTS.MESSAGES.INTERVIEW_SLOT_PICK(messageId),
      { slot }
    );
    return response.data.data;
  }

  static async cancelInterviewSlots(messageId: string): Promise<ConversationMessage> {
    const response = await getApiClient().post<ApiResponse<ConversationMessage>>(
      API_ENDPOINTS.MESSAGES.INTERVIEW_SLOT_CANCEL(messageId),
      {}
    );
    return response.data.data;
  }

  // ─── Messaging privacy ─────────────────────────────────────

  static async getPrivacy(): Promise<MessagingPrivacy> {
    const response = await getApiClient().get<ApiResponse<MessagingPrivacy>>(
      API_ENDPOINTS.MESSAGES.PRIVACY
    );
    return response.data.data;
  }

  static async updatePrivacy(changes: Partial<MessagingPrivacy>): Promise<MessagingPrivacy> {
    const response = await getApiClient().put<ApiResponse<MessagingPrivacy>>(
      API_ENDPOINTS.MESSAGES.PRIVACY,
      changes
    );
    return response.data.data;
  }

  // ─── Thread controls (only the viewer sees them) ──────────

  /** `until`: an ISO time, 'forever', or null to unmute. */
  static async mute(conversationId: string, until: string | null): Promise<void> {
    await getApiClient().put(API_ENDPOINTS.MESSAGES.CONVERSATION_MUTE(conversationId), { until });
  }

  static async setConversationPinned(conversationId: string, pinned: boolean): Promise<void> {
    const url = API_ENDPOINTS.MESSAGES.CONVERSATION_PIN(conversationId);
    if (pinned) await getApiClient().put(url, {});
    else await getApiClient().delete(url);
  }

  static async setArchived(conversationId: string, archived: boolean): Promise<void> {
    const url = API_ENDPOINTS.MESSAGES.CONVERSATION_ARCHIVE(conversationId);
    if (archived) await getApiClient().put(url, {});
    else await getApiClient().delete(url);
  }

  static async markUnread(conversationId: string): Promise<void> {
    await getApiClient().put(API_ENDPOINTS.MESSAGES.CONVERSATION_UNREAD(conversationId), {});
  }

  static async markAllRead(conversationIds: string[]): Promise<void> {
    if (conversationIds.length === 0) return;
    await getApiClient().post(API_ENDPOINTS.MESSAGES.READ_ALL, { conversationIds });
  }

  /** "Delete chat": clears the history for the viewer only. */
  static async deleteChat(conversationId: string): Promise<void> {
    await getApiClient().delete(API_ENDPOINTS.MESSAGES.CONVERSATION_HISTORY(conversationId));
  }

  static async setBlocked(conversationId: string, blocked: boolean): Promise<void> {
    const url = API_ENDPOINTS.MESSAGES.CONVERSATION_BLOCK(conversationId);
    if (blocked) await getApiClient().put(url, {});
    else await getApiClient().delete(url);
  }

  static async report(
    conversationId: string,
    body: {
      reason: MessageReportReason;
      details?: string;
      messageId?: string | null;
      block?: boolean;
    }
  ): Promise<{ id: string; blocked: boolean }> {
    const response = await getApiClient().post<ApiResponse<{ id: string; blocked: boolean }>>(
      API_ENDPOINTS.MESSAGES.CONVERSATION_REPORT(conversationId),
      body
    );
    return response.data.data;
  }

  /** Search message text in one thread, or across an inbox (personal, or a workspace id). */
  static async searchMessages(
    q: string,
    scope: { conversationId?: string; inbox?: string | null } = {}
  ): Promise<MessageSearchResult[]> {
    const response = await getApiClient().get<ApiResponse<MessageSearchResult[]>>(
      API_ENDPOINTS.MESSAGES.SEARCH,
      {
        params: {
          q,
          ...(scope.conversationId ? { conversationId: scope.conversationId } : {}),
          ...(scope.inbox ? { inbox: scope.inbox } : {}),
        },
      }
    );
    return response.data.data ?? [];
  }

  static async startConversation(otherUserId: string): Promise<Conversation> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<Conversation>>(
      API_ENDPOINTS.MESSAGES.START_CONVERSATION,
      { otherUserId }
    );
    return response.data.data;
  }

  static async getConversation(id: string): Promise<Conversation> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<Conversation>>(
      API_ENDPOINTS.MESSAGES.GET_CONVERSATION(id)
    );
    return response.data.data;
  }

  static async getMessages(
    conversationId: string,
    params?: GetMessagesParams
  ): Promise<PaginatedResponse<ConversationMessage>> {
    const client = getApiClient();
    const response = await client.get(API_ENDPOINTS.MESSAGES.GET_MESSAGES(conversationId), {
      params,
    });
    return response.data.data;
  }

  /**
   * Cursor pagination for backscroll: messages strictly older than `before`
   * (a message id), newest first. Stable while new messages keep arriving,
   * unlike offset pagination.
   */
  static async getMessagesBefore(
    conversationId: string,
    before: string,
    limit: number
  ): Promise<{ items: ConversationMessage[]; hasMore: boolean }> {
    const client = getApiClient();
    const response = await client.get<
      ApiResponse<{ items: ConversationMessage[]; hasMore: boolean }>
    >(API_ENDPOINTS.MESSAGES.GET_MESSAGES(conversationId), {
      params: { before, pageSize: limit },
    });
    return response.data.data;
  }

  /**
   * Share a photo (10 MB), video (25 MB) or document (10 MB). Photos and
   * videos can carry a small preview image and their size, made in the browser.
   */
  static async sendAttachment(
    conversationId: string,
    file: File,
    caption?: string,
    media?: { thumbnail?: Blob | null; width?: number; height?: number; durationMs?: number },
    onProgress?: (fraction: number) => void
  ): Promise<ConversationMessage> {
    const client = getApiClient();
    const form = new FormData();
    form.append('file', file);
    if (caption?.trim()) form.append('caption', caption.trim());
    if (media?.thumbnail) form.append('thumbnail', media.thumbnail, 'preview.jpg');
    if (media?.width) form.append('width', String(Math.round(media.width)));
    if (media?.height) form.append('height', String(Math.round(media.height)));
    if (media?.durationMs) form.append('durationMs', String(Math.round(media.durationMs)));
    const response = await client.post<ApiResponse<ConversationMessage>>(
      API_ENDPOINTS.MESSAGES.SEND_ATTACHMENT(conversationId),
      form,
      {
        timeout: 300_000,
        onUploadProgress: (e) => onProgress && e.total && onProgress(e.loaded / e.total),
      }
    );
    return response.data.data;
  }

  /** A shared photo/video (or its preview image) as a Blob, for showing in the page. */
  static async getAttachmentBlob(
    attachmentId: string,
    variant: 'original' | 'preview' = 'original'
  ): Promise<Blob> {
    const response = await getApiClient().get(
      API_ENDPOINTS.MESSAGES.DOWNLOAD_ATTACHMENT(attachmentId),
      { params: { variant, inline: 1 }, responseType: 'blob', timeout: 300_000 }
    );
    return response.data as Blob;
  }

  /** Media, files or links shared in a conversation (details panel). */
  static async getShared(
    conversationId: string,
    kind: 'media' | 'files' | 'links'
  ): Promise<SharedItem[]> {
    const response = await getApiClient().get<ApiResponse<SharedItem[]>>(
      API_ENDPOINTS.MESSAGES.SHARED(conversationId),
      { params: { kind } }
    );
    return response.data.data ?? [];
  }

  /** A preview card for a link, or null when the page has nothing to show. */
  static async getLinkPreview(url: string): Promise<LinkPreview | null> {
    const response = await getApiClient().get<ApiResponse<LinkPreview | null>>(
      API_ENDPOINTS.MESSAGES.LINK_PREVIEW,
      { params: { url } }
    );
    return response.data.data ?? null;
  }

  static async downloadAttachment(attachmentId: string, fileName: string): Promise<void> {
    const client = getApiClient();
    const response = await client.get(API_ENDPOINTS.MESSAGES.DOWNLOAD_ATTACHMENT(attachmentId), {
      responseType: 'blob',
    });
    downloadBlob(response.data, fileName);
  }

  static async sendMessage(
    conversationId: string,
    content: string,
    replyToId?: string | null
  ): Promise<ConversationMessage> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<ConversationMessage>>(
      API_ENDPOINTS.MESSAGES.SEND_MESSAGE(conversationId),
      { content, ...(replyToId ? { replyToId } : {}) }
    );
    return response.data.data;
  }

  // ─── Message actions ──────────────────────────────────────

  static async editMessage(messageId: string, content: string): Promise<ConversationMessage> {
    const response = await getApiClient().patch<ApiResponse<ConversationMessage>>(
      API_ENDPOINTS.MESSAGES.MESSAGE(messageId),
      { content }
    );
    return response.data.data;
  }

  static async deleteMessage(messageId: string, scope: 'me' | 'everyone'): Promise<void> {
    await getApiClient().delete(API_ENDPOINTS.MESSAGES.MESSAGE(messageId), { params: { scope } });
  }

  /** Your reaction (one per message); null removes it. */
  static async setReaction(messageId: string, emoji: string | null): Promise<ConversationMessage> {
    const client = getApiClient();
    const response = emoji
      ? await client.put<ApiResponse<ConversationMessage>>(
          API_ENDPOINTS.MESSAGES.REACTION(messageId),
          { emoji }
        )
      : await client.delete<ApiResponse<ConversationMessage>>(
          API_ENDPOINTS.MESSAGES.REACTION(messageId)
        );
    return response.data.data;
  }

  static async setPinned(messageId: string, pinned: boolean): Promise<ConversationMessage> {
    const client = getApiClient();
    const url = API_ENDPOINTS.MESSAGES.PIN(messageId);
    const response = pinned
      ? await client.put<ApiResponse<ConversationMessage>>(url, {})
      : await client.delete<ApiResponse<ConversationMessage>>(url);
    return response.data.data;
  }

  static async getPinned(conversationId: string): Promise<ConversationMessage[]> {
    const response = await getApiClient().get<ApiResponse<ConversationMessage[]>>(
      API_ENDPOINTS.MESSAGES.PINS(conversationId)
    );
    return response.data.data ?? [];
  }

  static async setStarred(messageId: string, starred: boolean): Promise<ConversationMessage> {
    const client = getApiClient();
    const url = API_ENDPOINTS.MESSAGES.STAR(messageId);
    const response = starred
      ? await client.put<ApiResponse<ConversationMessage>>(url, {})
      : await client.delete<ApiResponse<ConversationMessage>>(url);
    return response.data.data;
  }

  static async getStarred(): Promise<StarredMessageItem[]> {
    const response = await getApiClient().get<ApiResponse<StarredMessageItem[]>>(
      API_ENDPOINTS.MESSAGES.STARRED
    );
    return response.data.data ?? [];
  }

  static async forwardMessage(
    messageId: string,
    conversationIds: string[]
  ): Promise<ConversationMessage[]> {
    const response = await getApiClient().post<ApiResponse<ConversationMessage[]>>(
      API_ENDPOINTS.MESSAGES.FORWARD(messageId),
      { conversationIds }
    );
    return response.data.data;
  }

  static async getMessageInfo(messageId: string): Promise<MessageInfo> {
    const response = await getApiClient().get<ApiResponse<MessageInfo>>(
      API_ENDPOINTS.MESSAGES.INFO(messageId)
    );
    return response.data.data;
  }

  /** The other person in a one-to-one thread, for the profile panel. */
  static async getPeer(conversationId: string): Promise<ConversationPeer> {
    const response = await getApiClient().get<ApiResponse<ConversationPeer>>(
      API_ENDPOINTS.MESSAGES.PEER(conversationId)
    );
    return response.data.data;
  }

  static async markAsRead(conversationId: string): Promise<void> {
    const client = getApiClient();
    await client.patch(API_ENDPOINTS.MESSAGES.MARK_READ(conversationId), {});
  }

  // ─── College Messages page ────────────────────────────────

  /** This staff member's college inbox (threads they started or joined as staff). */
  static async getCollegeConversations(orgId: string, archived = false): Promise<Conversation[]> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<Conversation[]>>(
      API_ENDPOINTS.MESSAGES.COLLEGE_CONVERSATIONS(orgId),
      archived ? { params: { archived: 'true' } } : undefined
    );
    return response.data.data ?? [];
  }

  /** Opens (or reuses) a college thread with a student, staff member or partner recruiter. */
  static async startCollegeConversation(orgId: string, otherUserId: string): Promise<Conversation> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<Conversation>>(
      API_ENDPOINTS.MESSAGES.COLLEGE_CONVERSATIONS(orgId),
      { otherUserId }
    );
    return response.data.data;
  }

  /** People this staff member may message; `kinds` says which tabs they get. */
  static async getCollegeRecipients(
    orgId: string,
    params: { q?: string; kind?: MessageRecipientKind }
  ): Promise<{ items: MessageRecipient[]; kinds: MessageRecipientKind[] }> {
    const client = getApiClient();
    const response = await client.get<
      ApiResponse<{ items: MessageRecipient[]; kinds: MessageRecipientKind[] }>
    >(API_ENDPOINTS.MESSAGES.COLLEGE_RECIPIENTS(orgId), { params });
    return response.data.data;
  }
}
